import { ThreatType } from '@nirvana/shared/dtos/types/shared/threats';
import { EndpointType } from '@nirvana/shared/dtos/types/shared/filters';
import { Feature } from '@nirvana/shared/constants';
import { User } from '@nirvana/shared/user-helper/nestjs';
import { Filter } from '@nirvana/shared/dtos/types/generic-dashboards/gz-console';
import { Endpoint } from '@nirvana/shared/endpoints-helper/nestjs';
import { ProtectedEntities } from '@nirvana/shared/models/nestjs';
import { ReportingPeriod } from '@nirvana/shared/dtos/types/shared/time';
import { Target } from '@nirvana/shared/dtos/types/dashboards/common';
import { DonutChartWidgetPayload } from './shared-dtos-donut-widget';

export enum Status {
  ACTIVE = 0,
  EMPTY_STATE = 1,
  MISSING_LICENSE = 2,
  MISSING_LICENCE_MSP = 3,
}

export interface OverviewInformation {
  managedEndpoints: OverviewManagedEndpoints;
  activeEndpoints: OverviewActiveEndpoints;
  threatsBlocked: OverviewThreatsBlocked;
  companyScore: OverviewCompanyRiskScore;
  notAvailableFeatures?: Array<string>;
}

export interface GetOverviewInformationResponse {
  widgetResult: {
    managedEndpoints: OverviewManagedEndpoints;
    activeEndpoints: OverviewActiveEndpoints;
    threatsBlocked: OverviewThreatsBlocked;
    companyScore: OverviewCompanyRiskScore;
    notAvailableFeatures?: Array<string>;
  };
  token: string;
}

export interface OverviewManagedEndpoints {
  status: Status;
  data?: IntValueWithOptionalPercentageDelta;
}

export interface OverviewActiveEndpoints {
  status: Status;
  data?: number;
}

export interface OverviewThreatsBlocked {
  status: Status;
  data?: IntValueWithOptionalPercentageDelta;
}

export interface OverviewCompanyRiskScore {
  status: Status;
  data?: DoubleValueWithOptionalDelta;
}

export interface IntValueWithDelta {
  currentValue: number;
  delta: number;
}

export interface IntValueWithOptionalPercentageDelta {
  currentValue: number;
  delta?: number;
}

export interface DoubleValueWithOptionalDelta {
  currentValue: number;
  delta?: number;
}

export interface TopThreatsBlocked {
  threats: Array<ThreatWithDelta>;
  threatsByDate: Array<ThreatByDate>;
}

export interface GetTopThreatsBlockedResponse {
  widgetResult: {
    status: Status;
    data?: TopThreatsBlocked;
  };
  token: string;
}

export interface ThreatWithDelta {
  threat: Threat;
  delta: number;
}

export interface Threat {
  name: string;
  count: number;
  threatType: ThreatType;
}

export interface ThreatByDate {
  threat: Threat;
  date: string;
}

export interface ThreatsBreakdown {
  workstationNo: IntValueWithEndpointType;
  serversNo: IntValueWithEndpointType;
  containerHostsNo?: IntValueWithEndpointType;
  containersNo?: IntValueWithEndpointType;
}

export interface IntValueWithEndpointType {
  value: number;
  endpointType: EndpointType;
}

export interface GetThreatsBreakdownResponse {
  widgetResult: {
    status: Status;
    data?: ThreatsBreakdown;
    availableExtraFeatures?: Array<string>;
  };
  token: string;
}

export interface GetIncidentsStatusResponse {
  widgetResult: {
    status: Status;
    data?: IncidentsStatus;
  };
  token: string;
}

export interface IncidentsStatus {
  blockedIncidents?: number;
  investigatingIncidents?: number;
  partiallyBlockedIncidents?: number;
}

export const REMEDIATION_ACTIONS_NUM_DECIMALS = 4;

export interface RemediationActions {
  blocked: number;
  deleted: number;
  processesKilled: number;
  quarantined: number;
  disinfected: number;
}

export interface GetRemediationActionsResponse {
  widgetResult: {
    status: Status;
    data?: RemediationActions;
  };
  token: string;
}

export interface InventoryInformation {
  winWorkstations: IntValueWithDelta;
  winServers: IntValueWithDelta;
  macOS: IntValueWithDelta;
  linux: IntValueWithDelta;
  physicalEndpoints: IntValueWithDelta;
  virtualMachines: IntValueWithDelta;
  containerHosts?: IntValueWithDelta;
  containers?: IntValueWithDelta;
}

export interface InventoryInformationWithoutDelta {
  winWorkstations: number;
  winServers: number;
  macOS: number;
  linux: number;
  physicalEndpoints: number;
  virtualMachines: number;
  securityVirtualAppliances?: number;
  containerHosts?: number;
  containers?: number;
}

export interface GetInventoryInformationResponse {
  widgetResult: {
    status: Status;
    data?: InventoryInformation;
    availableExtraFeatures?: Array<string>;
  };
  token: string;
}

export interface GetEndpointModuleStatusResponse {
  widgetResult: {
    status: Status;
    data?: Array<EndpointModuleStatus>;
    networkNodeId?: string;
  };
  token: string;
}

export interface EndpointModuleStatus {
  name: string;
  enabled: number;
  disabled: number;
  notInstalled: number;
}

export interface GetCompanyRiskScoreResponse {
  widgetResult: {
    status: Status;
    data?: CompanyRiskScore;
  };
  token: string;
}

export interface CompanyRiskScore {
  companyRiskScore: number;
  categories: CompanyRiskScoreCategories;
}

export interface CompanyRiskScoreCategories {
  misconfiguration: DoubleValueWithOptionalDelta;
  appVulnerabilities: DoubleValueWithOptionalDelta;
  humanRisk: DoubleValueWithOptionalDelta;
}

export interface AccessDataProtection {
  deviceControl: number;
  connectionsBlocked: number;
  applications: number;
  websitesBlocked: number;
}

export interface GetAccessDataProtectionResponse {
  widgetResult: {
    status: Status;
    data?: AccessDataProtection;
  };
  token: string;
}

export interface WebsiteCategory {
  values: IntValueWithDelta;
  threatType?: ThreatType;
}

export interface WebsitesCategories {
  malware: WebsiteCategory;
  phishing: WebsiteCategory;
  fraud: WebsiteCategory;
}

export interface GetWebsitesBlockedResponse {
  widgetResult: {
    status: Status;
    data?: WebsitesCategories;
    total?: number;
  };
  token: string;
}

export interface BlockedNetworkAttackTechnique {
  values: IntValueWithDelta;
  threatType: ThreatType;
}

export interface BlockedNetworkAttackTechniques {
  initialAccess: BlockedNetworkAttackTechnique;
  credentialAccess: BlockedNetworkAttackTechnique;
  discovery: BlockedNetworkAttackTechnique;
  lateralMovement: BlockedNetworkAttackTechnique;
  crimeware: BlockedNetworkAttackTechnique;
}

export interface GetBlockedNetworkAttackTechniquesResponse {
  widgetResult: {
    status: Status;
    data?: BlockedNetworkAttackTechniques;
  };
  token: string;
}

// HEALTH, ASM AND EXECUTIVE SUMMARY DASHBOARD WIDGETS STREAMING START
export interface WidgetOverview {
  healthy: number;
  warning: number;
  critical: number;
}

export interface PolicyComplianceInfo {
  compliant: number;
  nonCompliant: number;
  offlineSinceLastChange: number;
  editedByPowerUser: number;
  inherited: number;
  directlyApplied: number;
  assignedWithPolicyRules: number;
}

export interface PolicyComplianceWithOverview {
  overview: WidgetOverview;
  policyCompliance: PolicyComplianceInfo;
}

export interface PermissionIssues {
  kernelExtensionNotApproved: number;
  diskAccessNotGranted: number;
  networkExtensionNotInstalled: number;
  tunnelingApplicationNotAllowed: number;
  certificateSSLNotTrusted: number;
}

export interface MacOsPermissionIssues {
  permissionIssues: PermissionIssues;
}

export interface InventoryOverview {
  managedEndpoints: number;
  activeEndpoints: number;
  offlineEndpoints: number;
  unmanagedEndpoints: number;
}

export interface EndpointSecurityIssues {
  // TODO TODO RE-ENABLE WHEN ENDPOINT SENDS THIS STATUS
  //notScanned: number;
  unsolvedMalwareDetections: number;
  settingsChangedWithPowerUser: number;
}

export interface EndpointCommunicationIssues {
  cannotConnectToGPN: number;
  isolated: number;
}

export interface EndpointModuleIssues {
  disabledModules: number;
  malfunctioningModules: number;
  expiredModules: number;
  modulesInGracePeriod: number;
}

export interface EndpointIssues {
  overview: WidgetOverview;
  endpointSecurityIssues: EndpointSecurityIssues;
  endpointCommunicationIssues: EndpointCommunicationIssues;
  endpointModuleIssues: EndpointModuleIssues;
}

export interface ProductUpdateInfo {
  updated: number;
  pendingRestart: number;
  updateDisabled: number;
  outdated: number;
}

export interface SignatureUpdateInfo {
  updated: number;
  offlineMoreThanOneDay: number;
  updateDisabled: number;
  outdated: number;
}

export enum ProductStatus {
  ACTIVE = 1,
  EXPIRED = 2,
  INVALID = 3,
  TRIAL = 4,
}

export interface LicenseInfo {
  name: string;
  status: ProductStatus;
}

export interface GetLicenseInfo {
  products: Array<LicenseInfo>;
  companySelectedIsUsersCompany?: boolean;
}

export interface PatchManagementOverview {
  updated: number;
  pendingRestart: number;
  downloadFailed: number;
  installationFailed: number;
  notInstalled: number;
}

export interface PatchManagementAssessmentInfo {
  neverChecked: number;
  noCheckSinceLastMonth: number;
}

export interface PatchManagementMissingPatches {
  nonSecurity: number;
  security: number;
}

export interface PatchManagementInfo {
  overview: PatchManagementOverview;
  assessmentInfo: PatchManagementAssessmentInfo;
  missingPatches: PatchManagementMissingPatches;
  patchManagementEndpointsCount: number;
}

export type PatchManagementResponse = PatchManagementInfo | HealthDashboardInfoStateData;

export interface HealthDashboardInfoStateData {
  hasInheritedLicense: boolean;
}

export interface HealthDashboardEndpointEncryptionSection {
  compliantEndpoints: number;
  uncompliantEndpoints: number;
}

export interface GzRiskIncidentOverviewSection {
  emails: number;
  gcpUsers: number;
}

export interface GzGeneralDonutChartSection {
  field: number;
}

export interface HealthDashboardEndpointEncryptionData {
  overview: HealthDashboardEndpointEncryptionSection;
}

export interface GzRiskIncidentOverviewData {
  overview: GzRiskIncidentOverviewSection;
}

export interface GzGeneralDonutChartData {
  data: GzGeneralDonutChartSection;
}

export interface SecurityServersStatusOverview {
  totalCount: number;
  underloaded: number;
  overloaded: number;
}

export interface SecurityServersStatusDataSection {
  outdated: number;
  turnedOff: number;
  storageScanningEnabled?: number;
  storageScanningDisabled?: number;
}

export interface SecurityServersStatusData {
  overview: SecurityServersStatusOverview;
  serversStatus: SecurityServersStatusDataSection;
}

export interface ProductUpdateStatus {
  overview: WidgetOverview;
  productInfo: ProductUpdateInfo;
}

export interface SignatureUpdateStatus {
  overview: WidgetOverview;
  signatureInfo: SignatureUpdateInfo;
}

export interface WidgetWithOverviewData
  extends Partial<EasmScheduleControllerPayload>,
    Partial<EasmVulnerableServersPayload>,
    Partial<EasmAssetsInventoryPayload>,
    Partial<EasmMostRecentAssetsPayload>,
    Partial<EasmVulnerableServicesPayload>,
    Partial<EasmOpenPortsPayload>,
    Partial<EasmServicesPerProtocolPayload>,
    Partial<EasmCriticalVulnerabilitiesPayload>,
    Partial<EasmDnsRecordsPayload>,
    Partial<EasmPeriodicAssetsPayload>,
    Partial<EasmAsnMapPayload>,
    Partial<EasmPeriodicVulnerabilitiesPayload>,
    Partial<EasmCertificateExpirationPayload>,
    Partial<EasmArtifactsInventoryPayload>,
    Partial<PhasrCategoryPayload>,
    Partial<PhasrAttackSurfaceReductionPayload>,
    Partial<PhasrTopRecommendationsPayload>,
    Partial<PhasrDetectedIncidentsPayload>,
    Partial<BlockedNetworkAttackTechniquesPayload>,
    Partial<TopThreatsBlockedPayload>,
    Partial<WebsitesBlockedPayload>,
    Partial<InventoryPayload>,
    Partial<ThreatsBreakdownPayload>,
    Partial<IncidentsStatusPayload>,
    Partial<RemediationActionsPayload>,
    Partial<CompanyRiskScorePayload>,
    Partial<AccessDataProtectionPayload>,
    Partial<OverviewPayload>,
    Partial<EndpointModulesStatusPayload>,
    RmGenericPayload,
    Partial<DonutChartWidgetPayload> {
  policyCompliance?: PolicyComplianceWithOverview;
  endpointIssues?: EndpointIssues;
  inventoryOverview?: InventoryOverview;
  macOsPermissionIssues?: MacOsPermissionIssues;
  licenseInfo?: GetLicenseInfo;
  endpointModulesStatus?: Array<EndpointModuleStatus>;
  patchManagement?: PatchManagementResponse;
  inventory?: InventoryInformationWithoutDelta;
  endpointsEncryption?: HealthDashboardEndpointEncryptionData | HealthDashboardInfoStateData;
  securityServersStatus?: SecurityServersStatusData | HealthDashboardInfoStateData;
  productUpdateStatus?: ProductUpdateStatus;
  signatureUpdateStatus?: SignatureUpdateStatus;

  // Risk Management specific fields
  identityMonitoringDisabled?: boolean;
}

export enum WidgetState {
  IS_ACTIVE = 0,
  IS_EMPTY = 1,
  IS_ERROR = 2,
  IS_LOADING = 3,
  IS_INFO = 4,
  IS_CHANGED = 5,
}

export interface GetWidgetWithOverviewResponse {
  state: WidgetState;
  data: WidgetWithOverviewData;
  widgetId: string;
}

export interface BlockedNetworkAttackTechniquesPayloadResponse extends GetWidgetWithOverviewResponse {
  data: BlockedNetworkAttackTechniquesPayload;
}

export interface TopThreatsBlockedPayloadResponse extends GetWidgetWithOverviewResponse {
  data: TopThreatsBlockedPayload;
}

export interface WebsitesBlockedPayloadResponse extends GetWidgetWithOverviewResponse {
  data: WebsitesBlockedPayload;
}

export interface InventoryPayloadResponse extends GetWidgetWithOverviewResponse {
  data: InventoryPayload;
}

export interface ThreatsBreakdownPayloadResponse extends GetWidgetWithOverviewResponse {
  data: ThreatsBreakdownPayload;
}

export interface IncidentsStatusPayloadResponse extends GetWidgetWithOverviewResponse {
  data: IncidentsStatusPayload;
}

export interface RemediationActionsPayloadResponse extends GetWidgetWithOverviewResponse {
  data: RemediationActionsPayload;
}

export interface CompanyRiskScorePayloadResponse extends GetWidgetWithOverviewResponse {
  data: CompanyRiskScorePayload;
}

export interface AccessDataProtectionPayloadResponse extends GetWidgetWithOverviewResponse {
  data: AccessDataProtectionPayload;
}

export interface OverviewPayloadResponse extends GetWidgetWithOverviewResponse {
  data: OverviewPayload;
}

export interface EndpointModulesStatusPayloadResponse extends GetWidgetWithOverviewResponse {
  data: EndpointModulesStatusPayload;
}

export interface EasmScheduleControllerPayloadResponse extends GetWidgetWithOverviewResponse {
  data: EasmScheduleControllerPayload;
}

export interface EasmVulnerableServersPayloadResponse extends GetWidgetWithOverviewResponse {
  data: EasmVulnerableServersPayload;
}

export interface EasmAssetsInventoryPayloadResponse extends GetWidgetWithOverviewResponse {
  data: EasmAssetsInventoryPayload;
}

export interface EasmArtifactsInventoryPayloadResponse extends GetWidgetWithOverviewResponse {
  data: EasmArtifactsInventoryPayload;
}

export interface EasmMostRecentAssetsPayloadResponse extends GetWidgetWithOverviewResponse {
  data: EasmMostRecentAssetsPayload;
}

export interface EasmVulnerableServicesPayloadResponse extends GetWidgetWithOverviewResponse {
  data: EasmVulnerableServicesPayload;
}

export interface EasmOpenPortsPayloadResponse extends GetWidgetWithOverviewResponse {
  data: EasmOpenPortsPayload;
}

export interface EasmServicesPerProtocolPayloadResponse extends GetWidgetWithOverviewResponse {
  data: EasmServicesPerProtocolPayload;
}

export interface EasmCriticalVulnerabilitiesPayloadResponse extends GetWidgetWithOverviewResponse {
  data: EasmCriticalVulnerabilitiesPayload;
}

export interface EasmDnsRecordsPayloadResponse extends GetWidgetWithOverviewResponse {
  data: EasmDnsRecordsPayload;
}

export interface EasmPeriodicAssetsPayloadResponse extends GetWidgetWithOverviewResponse {
  data: EasmPeriodicAssetsPayload;
}

export interface EasmPeriodicVulnerabilitiesPayloadResponse extends GetWidgetWithOverviewResponse {
  data: EasmPeriodicVulnerabilitiesPayload;
}

export interface EasmAsnMapPayloadResponse extends GetWidgetWithOverviewResponse {
  data: EasmAsnMapPayload;
}

export interface EasmCertificateExpirationPayloadResponse extends GetWidgetWithOverviewResponse {
  data: EasmCertificateExpirationPayload;
}

export interface PhasrCategoryPayloadResponse extends GetWidgetWithOverviewResponse {
  data: PhasrCategoryPayload;
}

export interface PhasrAttackSurfaceReductionPayloadResponse extends GetWidgetWithOverviewResponse {
  data: PhasrAttackSurfaceReductionPayload;
}

export interface PhasrTopRecommendationsPayloadResponse extends GetWidgetWithOverviewResponse {
  data: PhasrTopRecommendationsPayload;
}

export interface PhasrDetectedIncidentsPayloadResponse extends GetWidgetWithOverviewResponse {
  data: PhasrDetectedIncidentsPayload;
}

export interface GetScheduleControllerResponse {
  state: WidgetState;
  data: ScheduleControllerData;
  widgetId: string;
}

export enum EASMScanState {
  IDLE = 0,
  RUNNING = 1,
  FINISHED = 2,
  STOPPED = 3,
  ERROR = 4,
}

export interface ExecutiveSummaryPayload {
  targets?: Array<Target>;
  reportingPeriod?: ReportingPeriod;
}

export interface BlockedNetworkAttackTechniquesPayload extends ExecutiveSummaryPayload {
  blockedNetworkAttackTechniques: GetBlockedNetworkAttackTechniquesResponse;
}

export interface TopThreatsBlockedPayload extends ExecutiveSummaryPayload {
  topThreatsBlocked: GetTopThreatsBlockedResponse;
}

export interface WebsitesBlockedPayload extends ExecutiveSummaryPayload {
  websitesCategories: GetWebsitesBlockedResponse;
}

export interface InventoryPayload extends ExecutiveSummaryPayload {
  inventoryInformation: GetInventoryInformationResponse;
}

export interface ThreatsBreakdownPayload extends ExecutiveSummaryPayload {
  threatsBreakdown: GetThreatsBreakdownResponse;
}

export interface IncidentsStatusPayload extends ExecutiveSummaryPayload {
  incidentsStatus: GetIncidentsStatusResponse;
}

export interface RemediationActionsPayload extends ExecutiveSummaryPayload {
  remediationActions: GetRemediationActionsResponse;
}

export interface CompanyRiskScorePayload extends ExecutiveSummaryPayload {
  companyRiskScore: GetCompanyRiskScoreResponse;
}

export interface AccessDataProtectionPayload extends ExecutiveSummaryPayload {
  accessDataProtection: GetAccessDataProtectionResponse;
}

export interface OverviewPayload extends ExecutiveSummaryPayload {
  overviewInformation: GetOverviewInformationResponse;
}

export interface EndpointModulesStatusPayload extends ExecutiveSummaryPayload {
  endpointModuleStatus: GetEndpointModuleStatusResponse;
}

export interface EasmVulnerableServicesPayload {
  easmVulnerableServices: VulnerableServicesData[];
  inventorySmartViewId?: string;
  total: number;
}

export interface EasmPeriodicAssetsPayload {
  easmPeriodicAssets: PeriodicAssetsData[];
}

export interface EasmPeriodicVulnerabilitiesPayload {
  easmPeriodicVulnerabilities: PeriodicVulnerabilitiesData[];
}

export interface EasmAsnMapPayload {
  easmAsnMap: AnsMapData[];
  inventorySmartViewId?: string;
}

export interface EasmCertificateExpirationPayload {
  easmCertificateExpiration: CertificateExpirationData[];
  inventorySmartViewId?: string;
}

export interface EasmScheduleControllerPayload {
  easmSchedule: ScheduleControllerData;
}

export interface EasmAssetsInventoryPayload {
  easmAssetsInventory: EasmAssetsInventoryData;
}

export interface EasmArtifactsInventoryPayload {
  easmArtifactsInventory: EasmArtifactsInventoryData;
}

export interface EasmMostRecentAssetsPayload {
  easmMostRecentAssets: EasmMostRecentAssetsData[];
  inventorySmartViewId?: string;
}

export interface EasmMostRecentAssetsData {
  name: string;
  dateAdded: string;
  userName: string;
}

export interface PeriodicAssetsData {
  date: string;
  totalAssets: number;
}

export interface PeriodicVulnerabilitiesData {
  date: string;
  totalVulnerabilities: number;
}

export interface AnsMapData {
  countryCode: string;
  value: number;
}

export interface CertificateExpirationData {
  validity: number;
  count: number;
}

export interface VulnerableServicesData {
  name: string;
  cvesLength: number;
}

export interface EasmAssetsInventoryData {
  domains: DiscoveredAssetsData;
  ipAddresses: DiscoveredAssetsData;
  emails: DiscoveredAssetsData;
}

export interface EasmArtifactsInventoryData {
  asnReports: DiscoveredAssetsData;
  certificateReports: DiscoveredAssetsData;
  dnsRecords: DiscoveredAssetsData;
  ipBlocks: DiscoveredAssetsData;
  services: DiscoveredAssetsData;
  allArtifactsSmartViewId: string;
}

interface DiscoveredAssetsData {
  total: number;
  inventorySmartViewId?: string;
}

export interface ScheduleControllerData {
  lastScanDate: string | null;
  nextScanDate: string | null;
  scanState?: EASMScanState;
  error?: string;
}

export interface EasmVulnerableServersPayload {
  vulnerableServers: VulnerableServersData;
  inventorySmartViewId?: string;
}

export interface VulnerableServersData {
  total: number;
}

export interface EasmOpenPortsPayload {
  openPorts: TopData;
  inventorySmartViewId?: string;
}

export interface EasmServicesPerProtocolPayload {
  servicesPerProtocol: TopData;
  inventorySmartViewId?: string;
}

export interface EasmCriticalVulnerabilitiesPayload {
  criticalVulnerabilities: TopData;
  inventorySmartViewId?: string;
}

export interface PhasrCategoryPayload {
  isLearning: boolean;
  companyId: string;
  learning?: PhasrLearningPayload;
  slices?: PhasrSlicePayload;
  recommendations?: PhasrRecommendationData[];
}

export interface PhasrLearningPayload {
  progress?: number;
  estimatedRemainingDays?: number;
  targetUsersCount: number;
  behavioralPatternsCount?: number;
}

export interface PhasrSlicePayload {
  restricted: PhasrSlicesConfigDataWithDetails;
  active: PhasrSlicesConfigData;
  inactive: PhasrSlicesConfigData;
}

export interface PhasrAttackSurfaceReductionPayload {
  isLearning: boolean;
  learning?: PhasrLearningPayload;
  surfacePercentageReduction?: number;
}

export interface PhasrTopRecommendationsPayload {
  categoryRecommendations: PhasrCategoryRecommendationsData;
  companyId: string;
}

export interface PhasrCategoryRecommendationsData {
  global: PhasrRecommendationData[];
  group: PhasrRecommendationData[];
}

export interface PhasrRecommendationData {
  recommendationId: string;
  userProfilesCount: number;
  surfacePercentageReduction: number;
  recommendationType: number;
}

export interface PhasrSlicesConfigData {
  userProfilesCount: number;
}

export interface PhasrSlicesConfigDataWithDetails {
  userProfilesCount: number;
  details: PhasrSlicesDetailsData;
}

export interface PhasrSlicesDetailsData {
  autopilot: PhasrSlicesConfigData;
  directControl: PhasrSlicesConfigData;
}

export interface PhasrDetectedIncidentsPayload {
  companyId?: string;
  TamperingTool?: number;
  Lolbin?: number;
  Miner?: number;
  HackTool?: number;
  RemoteTool?: number;
}

export enum PhasrCategory {
  TAMPERING_TOOL = 1,
  HACKTOOL = 2,
  REMOTE_TOOL = 3,
  MINER = 4,
  LOLBIN = 5,
}

export enum DetectedIncidentsTemplatesStringKeys {
  LIVING_OFF_THE_LAND_BINARIES = 'Lolbin',
  REMOTE_ADMIN_TOOLS = 'RemoteTool',
  TAMPERING_TOOLS = 'TamperingTool',
  HACKTOOLS = 'HackTool',
  MINERS = 'Miner',
}

export const PHASR_CATEGORIES_MAPPING = {
  [PhasrCategory.LOLBIN]: DetectedIncidentsTemplatesStringKeys.LIVING_OFF_THE_LAND_BINARIES,
  [PhasrCategory.MINER]: DetectedIncidentsTemplatesStringKeys.MINERS,
  [PhasrCategory.TAMPERING_TOOL]: DetectedIncidentsTemplatesStringKeys.TAMPERING_TOOLS,
  [PhasrCategory.HACKTOOL]: DetectedIncidentsTemplatesStringKeys.HACKTOOLS,
  [PhasrCategory.REMOTE_TOOL]: DetectedIncidentsTemplatesStringKeys.REMOTE_ADMIN_TOOLS,
};

export const PHASR_CATEGORY_LABELS = Object.values(PHASR_CATEGORIES_MAPPING);

export interface TopData {
  rowsData: Array<RowData>;
  total: number;
}

export interface RowData {
  label: string;
  value: number | SeverityData;
}

export interface SeverityData {
  severity: SEVERITY;
  percentage: number;
}

export enum SEVERITY {
  HIGH = 1,
  MEDIUM = 2,
  LOW = 3,
}

export const SEVERITY_HIGH_LIMIT = 60;
export const SEVERITY_MEDIUM_LIMIT = 30;

export interface EasmDnsRecordsPayload {
  dnsRecords: DnsRecordsData;
  inventorySmartViewId?: string;
}

export interface DnsRecordsData {
  records: DnsRecord[];
}

export interface DnsRecord {
  label: string;
  value: number;
}

export interface CsvColumnDefinition {
  label: string;
  pathToValue: string;
  transformFn?: (value: any) => any;
}

export interface FeatureIdToValue {
  [featureId: number]: boolean;
}

export interface CommonCache {
  managedProtectedEntities?: Array<ProtectedEntities>;
  unmanagedProtectedEntities?: Array<any>;
  managedEndpoints?: Array<Endpoint>;
  unmanagedEndpoints?: Array<any>;
}

export type CommonCacheKeys = keyof CommonCache;

export interface CompanyDetailedInfo {
  _id: string;
  name: string;
  path: string;
}

export interface WidgetsSharedData {
  companyId: string;
  companyDetailedInfo?: CompanyDetailedInfo;
  userId: string;
  lng: string;
  timestamp: number;
  authContext?: User;
  filters: Array<Filter>;
  companyFeatures?: FeatureIdToValue;
  commonCache?: CommonCache;
}

export enum CacheableCollections {
  PROTECTED_ENTITIES = 'protectedEntities',
  ENDPOINTS = 'endpoints',
}

export interface WidgetCollectionToProjectionMap {
  collection: CacheableCollections;
  projection: Array<string>;
}

export interface WidgetDefinition {
  requiresAuthContext?: boolean;
  requiredCompanyFeatures?: Array<Feature>;
  requiredCommonCache?: Array<CommonCacheKeys>;
  collections?: Array<WidgetCollectionToProjectionMap>;
  requiredCompanyData?: Array<string>; // projection
}

export enum PATCH_STATUS {
  NOT_INSTALLED = 0,
  INSTALLED = 1,
  PENDING = 2,
  FAILED = 3,
}

// HEALTH, ASM AND EXECUTIVE SUMMARY DASHBOARD WIDGETS STREAMING END

// region Risk Management
interface RmGenericPayload
  extends RmTopFindingsWithSeverityPayload,
    RmTopVulnerabilitiesBySeverityPayload,
    RmTopIdentityRisksBySeverityPayload,
    RmTopFindingsByImpactPayload,
    RmTopVulnerabilitiesByImpactPayload,
    RmTopIdentityRisksByImpactPayload,
    RmTopResourcesAtRiskPayload,
    RmTopIdentitiesAtRiskPayload,
    RiskManagementResourcesByOSPayload,
    RmScoreOverTimePayload,
    RmCompanyStatePayload,
    RmOldestVulnerabilitiesPayload,
    RmCvesBySeverityPayload,
    RmApplicationBySeverityPayload {}

export interface RmTopFindingsWithSeverityPayload {
  topFindingsBySeverity?: {
    companyId: string;
    total: number;
    items?: { id: number; score: number }[];
  };
}

export interface RmTopVulnerabilitiesBySeverityPayload {
  topVulnerabilitiesBySeverity?: {
    companyId: string;
    total: number;
    items?: { applicationName: string; score: number }[];
  };
}

export interface RmOldestVulnerabilitiesPayload {
  oldestVulnerabilities?: {
    companyId: string,
    total: number,
    items?: { applicationName: string; cveCount: number, score: number, date: string }[],
  }
}

export interface RmCvesBySeverityPayload {
  topCvesBySeverity?: {
    companyId: string,
    totalCvesCount?: number,
    activelyExploitedCount?: number,
    totalNonZeroScore: number,
    items?: { cve: string, score: number }[],
  }
}

export interface RmApplicationBySeverityPayload {
  topApplicationsBySeverity?: {
    companyId: string,
    totalCvesCount?: number,
    activelyExploitedCount?: number,
    totalNonZeroScore: number,
    items?: { name: string, cveCount: number, score: number }[],
  }
}

export interface RmTopIdentityRisksBySeverityPayload {
  topIdentityRisksBySeverity?: {
    companyId: string;
    identityMonitoringDisabled: boolean;
    total: number;
    items?: { id: number; score: number }[];
  };
}

export interface RmTopFindingsByImpactPayload {
  topFindingsByImpact?: {
    companyId: string;
    total: number;
    items?: {
      indicatorId: number;
      affectedDevices: AffectedDeviceGroup[];
      totalAffectedDevices: number;
    }[];
  };
}

export interface RmTopVulnerabilitiesByImpactPayload {
  topVulnerabilitiesByImpact?: {
    companyId: string;
    total: number;
    items?: {
      applicationName: string;
      affectedDevices: AffectedDeviceGroup[];
      totalAffectedDevices: number;
    }[];
  };
}

export interface RmTopIdentityRisksByImpactPayload {
  topIdentityRisksByImpact?: {
    companyId: string;
    identityMonitoringDisabled: boolean;
    total: number;
    items?: {
      indicatorId: number;
      affectedUsers: AffectedUserGroup[];
      totalAffectedUsers: number;
    }[];
  };
}

interface AffectedDeviceGroup {
  type: number;
  count: number;
}

interface AffectedUserGroup {
  type: number;
  count: number;
}

export interface RmTopResourcesAtRiskPayload {
  topResourcesAtRisk?: {
    companyId: string;
    total: number;
    items?: {
      id: string;
      type: number;
      name: string;
      scoreBreakdown: RmScoreBreakdown;
    }[];
  };
}

export interface RmTopIdentitiesAtRiskPayload {
  topIdentitiesAtRisk?: {
    companyId: string;
    identityMonitoringDisabled: boolean;
    total: number;
    items?: {
      username: string;
      type: number;
      fullName: string;
      scoreBreakdown: RmScoreBreakdown;
    }[];
  };
}

export interface RmScoreBreakdown {
  baseScore: number;
  finalScore: number;
  multiplier: {
    endpoints?: number;
    industry?: number;
    exploitabilityScore?: number;
    cvesCountScore?: number;
    applications?: number;
    users?: number;
  };
}

export interface RiskManagementResourcesByOSPayloadResponse extends GetWidgetWithOverviewResponse {
  data: RiskManagementResourcesByOSPayload;
}

export interface RiskManagementResourcesByOSPayload {
  resourcesByOS?: ResourcesByOSData;
}

export interface ResourcesByOSData {
  companyId: string;
  items: {
    operatingSystem: number;
    counts: ResourcesTypes[];
  }[];
}

export interface ResourcesTypes {
  endpointType: number;
  count: number;
}

export interface RmScoreOverTimePayloadResponse extends GetWidgetWithOverviewResponse {
  data: RmScoreOverTimePayload;
}

export interface RmScoreOverTimePayload {
  scoreOverTime?: ScoreOverTime;
}

export interface ScoreOverTime {
  companyId: string;
  items: ScoreOverTimeItem[];
}

export interface ScoreOverTimeItem {
  score?: number;
  date: string;
}

export interface RmCompanyStateModifiers {
  industryScore: number;
}

export interface RmCompanyState {
  companyId: string;
  found: boolean;
  score: number;
  breakdown: {
    baseScore: number;
    identitiesScore: number;
    resourcesScore: number;
    modifiersScore: number;
    modifiers: RmCompanyStateModifiers;
  },
  distribution: {
    findings: number;
    vulnerabilities: number;
    identityRisks: number;
  }
  identityMonitoringDisabled: boolean;
}

export interface RmCompanyStatePayload {
  companyState?: RmCompanyState;
}


// endregion
